/**
 * Left-panel dataset/field tree for the report builder.
 *
 * Shows datasets grouped under collapsible sections; each field is a
 * draggable card that publishes its `BuilderFieldDef` through the
 * HTML5 drag/drop dataTransfer (key: `field`). AxisDropzone reads it
 * back when dropped.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical, Loader2 } from 'lucide-react';
import {
  type BuilderDataset,
  type BuilderFieldDef,
  type DsidCatalogResponse,
} from '../../../lib/api/reporting';

export const FIELD_DRAG_TYPE = 'field';
/** Prefix for per-kind drag MIME types, so dropzones can reject during dragover. */
export const FIELD_KIND_DRAG_PREFIX = 'field-kind/';

interface FieldPickerTreeProps {
  catalog: DsidCatalogResponse | null;
  loading?: boolean;
  /** Optional filter input (e.g. user-typed field search). */
  search?: string;
  onSearchChange?: (next: string) => void;
}

/**
 * Encodes a field for the HTML5 drag dataTransfer payload.
 *
 * @param field - The dragged BuilderFieldDef
 */
export function encodeFieldDrag(field: BuilderFieldDef): string {
  return JSON.stringify(field);
}

/**
 * Decodes a field that was previously encoded with `encodeFieldDrag`.
 * Returns `null` if the payload is missing or malformed.
 */
export function decodeFieldDrag(raw: string | null | undefined): BuilderFieldDef | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as BuilderFieldDef;
    if (typeof parsed.dsid !== 'number' || typeof parsed.field_id !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

interface FieldRowProps {
  field: BuilderFieldDef;
}

function FieldRow({ field }: FieldRowProps) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(FIELD_DRAG_TYPE, encodeFieldDrag(field));
        e.dataTransfer.setData(`${FIELD_KIND_DRAG_PREFIX}${field.kind}`, '');
        e.dataTransfer.effectAllowed = 'copy';
      }}
      className="flex items-start gap-2 px-2 py-1.5 rounded border border-border bg-card hover:bg-accent/40 cursor-grab active:cursor-grabbing"
      role="treeitem"
    >
      <GripVertical className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium leading-tight truncate">{field.label}</div>
        {field.short_label && field.short_label !== field.label && (
          <div className="text-[10px] text-muted-foreground truncate">{field.short_label}</div>
        )}
      </div>
    </div>
  );
}

interface DatasetSectionProps {
  dataset: BuilderDataset;
  defaultOpen: boolean;
  search: string;
}

function DatasetSection({ dataset, defaultOpen, search }: DatasetSectionProps) {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  const filtered = search
    ? dataset.fields.filter((f) =>
      f.label.toLowerCase().includes(search.toLowerCase())
      || f.short_label.toLowerCase().includes(search.toLowerCase())
    )
    : dataset.fields;
  if (search && filtered.length === 0) return null;

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 w-full px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
      >
        {open
          ? <ChevronDown className="h-3.5 w-3.5" />
          : <ChevronRight className="h-3.5 w-3.5" />}
        <span>{dataset.label}</span>
        <span className="ml-auto text-[10px] font-normal">{filtered.length}</span>
      </button>
      {open && (
        <div className="space-y-1 px-2 pb-2">
          {filtered.map((field) => (
            <FieldRow key={`${field.dsid}-${field.field_id}`} field={field} />
          ))}
        </div>
      )}
    </div>
  );
}

export function FieldPickerTree({ catalog, loading, search, onSearchChange }: FieldPickerTreeProps) {
  if (loading && !catalog) {
    return (
      <div className="flex items-center justify-center h-[300px] text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  const datasets = catalog?.datasets || [];

  return (
    <div className="flex flex-col h-full" role="tree" aria-label="Available fields">
      {onSearchChange && (
        <div className="p-2 border-b border-border">
          <input
            type="text"
            placeholder="Search fields..."
            value={search || ''}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full px-2 py-1 text-xs border border-input rounded bg-background"
          />
        </div>
      )}
      <div className="flex-1 overflow-y-auto">
        {datasets.length === 0 ? (
          <div className="p-4 text-center text-xs text-muted-foreground">
            No fields available
          </div>
        ) : (
          datasets.map((ds, idx) => (
            <DatasetSection
              key={ds.dsid}
              dataset={ds}
              defaultOpen={idx === 0}
              search={search || ''}
            />
          ))
        )}
      </div>
    </div>
  );
}
